// 설문 항목 정의 · 설문 사전 — 추출 엔진과 템플릿 편집기가 함께 쓴다
//
// 원천: /API/studyExport 의 SurveyDictionary(⑰) · Surveys(⑱)  (SQL: study_export_survey.sql)
//   전용 테이블 설문(PSQI · ISI)은 문항이 화면 코드에 고정돼 있어 여기 고정 목록을 둔다.
//     item 키 = DB 열 이름 (InfoStudySubjectPSQI / InfoStudySubjectISI). 보기 답은 이미 번호(0~)로 저장돼 있다.
//     입력 화면 문항 정의: userComponents/surveyQuestions/psqiQuestions.js · isiQuestions.js
//   범용 설문은 SurveyDictionary 의 문항(QuestionID)을 쓴다. 답은 보기 글자로 저장돼 있어 OptionsJSON 의 value(번호)로 바꾼다.
//
// 항목 종류(kind)
//   clock  : 시각 "HH:mm" (취침 · 기상)      number : 숫자 (분 · 시간 · 점수)
//   choice : 보기 번호                         text   : 글자

export const SURVEY_ITEM_KINDS = ['clock', 'number', 'choice', 'text'];

const item = (item, name, kind) => ({ item, name, kind });

export const FIXED_SURVEY_ITEMS = {
  PSQI: [
    item('Q1', '취침 시각', 'clock'),
    item('Q2', '잠들기까지 걸린 시간(분)', 'number'),
    item('Q3', '기상 시각', 'clock'),
    item('Q4', '실제 수면 시간(시간)', 'number'),
    item('Q5A', '30분 이내 잠들지 못함', 'choice'),
    item('Q5B', '수면 중 자주 깸', 'choice'),
    item('Q5C', '화장실에 가려고 깸', 'choice'),
    item('Q5D', '숨쉬기 어려움', 'choice'),
    item('Q5E', '기침 · 코골이', 'choice'),
    item('Q5F', '추워서 잠들기 어려움', 'choice'),
    item('Q5G', '더워서 잠들기 어려움', 'choice'),
    item('Q5H', '나쁜 꿈', 'choice'),
    item('Q5I', '통증', 'choice'),
    item('Q5JText', '기타 사유', 'text'),
    item('Q5J', '기타 사유 빈도', 'choice'),
    item('Q6', '전반적 수면의 질', 'choice'),
    item('Q7', '수면제 복용', 'choice'),
    item('Q8', '주간 졸림', 'choice'),
    item('Q9', '업무 수행 어려움', 'choice'),
    item('SubjQuality', 'C1 주관적 수면 질', 'number'),
    item('SleepLatency', 'C2 수면 잠복기', 'number'),
    item('SleepDuration', 'C3 수면 시간', 'number'),
    item('SleepEfficiency', 'C4 수면 효율', 'number'),
    item('SleepDisturbance', 'C5 수면 방해', 'number'),
    item('SleepMedication', 'C6 수면제 사용', 'number'),
    item('DaytimeDysfunction', 'C7 주간 기능장애', 'number'),
    item('TotalScore', '총점', 'number'),
  ],
  ISI: [
    item('Q1A', '잠들기 어려움', 'choice'),
    item('Q1B', '수면 유지 어려움', 'choice'),
    item('Q1C', '너무 일찍 깸', 'choice'),
    item('Q2', '수면 패턴 만족도', 'choice'),
    item('Q3', '일상생활 방해', 'choice'),
    item('Q4', '타인이 알아차림', 'choice'),
    item('Q5', '걱정 · 스트레스', 'choice'),
    item('TotalScore', '총점', 'number'),
  ],
};

// 시각 항목 중 기본으로 "자정 기준 상대 시간"으로 내보내는 것 (취침 시각: 23:30 → -0.5)
export const DEFAULT_CLOCK_OFFSET_ITEMS = new Set(['PSQI|Q1']);

export const surveyRef = (surveyCode, itemKey) => `${surveyCode}|${itemKey}`;

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const isFlag = (v) => v === true || v === 'true' || Number(v) === 1;
const visitKeyOf = (d) => (isFlag(d.IsRepeatVisit) ? String(d.RegimenCode) : String(d.VisitCode ?? `${d.RegimenCode}${d.Cycle ?? ''}`));

export function parseOptions(optionsJson) {
  if (!optionsJson) return [];
  try {
    const list = typeof optionsJson === 'string' ? JSON.parse(optionsJson) : optionsJson;
    return Array.isArray(list) ? list.filter((o) => o && 'text' in o) : [];
  } catch {
    return [];
  }
}

const KIND_OF_ANSWER_TYPE = { single: 'choice', multi: 'choice', multiple: 'choice', radio: 'choice', checkbox: 'choice' };

// 범용 설문 문항 이름: "3. 문항 내용" (머리글로 쓰기에 너무 길면 자른다)
const questionName = (d) => {
  const text = String(d.QuestionText ?? '').replace(/\s+/g, ' ').trim();
  const short = text.length > 40 ? `${text.slice(0, 40)}…` : text;
  return d.QuestionOrder !== null && d.QuestionOrder !== undefined ? `${d.QuestionOrder}. ${short}` : short || `Q${d.QuestionID}`;
};

/**
 * 설문 사전(⑰ 행) → 설문 목록
 * @returns {{ surveys: { surveyCode, surveyId, name, items: object[] }[], byRef: Map }}
 *   item = { surveyCode, item, name, kind, options, groupName, visitKeys }
 */
export function buildSurveyCatalog(surveyDictionary) {
  const surveys = new Map();
  const byRef = new Map();
  const sorted = [...(surveyDictionary ?? [])].sort((a, b) => num(a.VisitOrder) - num(b.VisitOrder));

  for (const d of sorted) {
    const code = d.SurveyCode;
    if (!code) continue;
    if (!surveys.has(code)) {
      surveys.set(code, { surveyCode: code, surveyId: num(d.SurveyID), name: d.SurveyNameKo || d.SurveyNameEn || code, items: [] });
    }
    const survey = surveys.get(code);
    const vk = visitKeyOf(d);

    const defs = FIXED_SURVEY_ITEMS[code]
      ? FIXED_SURVEY_ITEMS[code].map((f) => ({ ...f, options: [], groupName: null }))
      : d.QuestionID !== null && d.QuestionID !== undefined
        ? [{
          item: String(d.QuestionID),
          name: questionName(d),
          kind: KIND_OF_ANSWER_TYPE[d.AnswerType] ?? 'text',
          options: parseOptions(d.OptionsJSON),
          groupName: d.GroupName ?? null,
        }]
        : [];

    for (const def of defs) {
      const ref = surveyRef(code, def.item);
      if (!byRef.has(ref)) {
        const entry = { surveyCode: code, ...def, visitKeys: [] };
        byRef.set(ref, entry);
        survey.items.push(entry);
      }
      const entry = byRef.get(ref);
      if (!entry.visitKeys.includes(vk)) entry.visitKeys.push(vk);
    }
  }
  return { surveys: [...surveys.values()], byRef };
}

/**
 * 저장된 답 → 내보낼 값. 보기 문항은 글자를 번호(OptionsJSON value)로 바꾼다 (복수 선택은 "0,2").
 * 전용 테이블 설문은 이미 번호라 그대로.
 */
export function answerValue(raw, entry) {
  if (raw === null || raw === undefined) return '';
  if (entry?.kind !== 'choice' || !entry.options?.length) return raw;
  const toCode = (text) => {
    const hit = entry.options.find((o) => String(o.text).trim() === String(text).trim());
    return hit ? hit.value : text;
  };
  let list = raw;
  if (typeof raw === 'string' && raw.trim().startsWith('[')) {
    try { list = JSON.parse(raw); } catch { list = raw; }
  }
  if (Array.isArray(list)) return list.map(toCode).join(',');
  return toCode(raw);
}
